import React, { useEffect, useState } from "react";
import Layout from "../../../layout";
import BackButton from "../../../components/BackButton";
import "./onboarding.css";


const PLAYLIST_URL = "https://youtube.com/playlist?list=PLChhxxJ9lxkmnNy8tEckQ7xUp-WhR-RCA&si=LhjuhBQZmEXr-lTI";

const VideoEmbed = ({ src, title, youtubeUrl }) => {
  const [showOverlay, setShowOverlay] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleOverlayClick = () => {
    setShowOverlay(false);
  };

  return (
    <div className="video-wrapper" tabIndex="0" role="region" aria-label={`Video: ${title}`}>
      {showOverlay && isMobile ? (
        <div className="video-thumbnail" onClick={handleOverlayClick} role="button" aria-label={`Load video: ${title}`}>
          <div className="play-overlay">
            <svg className="play-icon" viewBox="0 0 68 48" aria-hidden="true">
              <path d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z" fill="#f00"></path>
              <path d="M 45,24 27,14 27,34" fill="#fff"></path>
            </svg>
          </div>
          <img 
            src={`https://img.youtube.com/vi/${src.split('/').pop()}/hqdefault.jpg`} 
            alt={`${title} video thumbnail`}
            className="video-thumbnail-img"
          />
        </div>
      ) : (
        <iframe
          src={src}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          className="video-iframe"
        ></iframe>
      )}
      <a 
        href={youtubeUrl} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="watch-on-youtube"
        aria-label={`Watch ${title} on YouTube`}
      >
        Watch on YouTube →
      </a>
    </div>
  );
};

const Onboarding = () => {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  return (
    <Layout>
      <div className="onboarding-container">
        <BackButton />
        <h1 className="ml-2">Welcome to WorkBrook!</h1>
        <p className="ml-2">
          Please find below a quick overview of the main features available on
          the platform:{" "}
        </p>

        <div className="playlist-button-wrapper">
          <a 
            href={PLAYLIST_URL} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="playlist-button"
            aria-label="Watch all onboarding videos playlist"
          >
            <svg className="playlist-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"></path>
              <polygon points="10 8 16 12 10 16 10 8"></polygon>
            </svg>
            Click to Watch Full Onboarding Playlist
          </a>
        </div>

        <div id="getting-started" className="mt-2 pl-2">
          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">
              Getting Started on Workbrook
            </h2>
            <p className="ml-2">
              Welcome to Workbrook! This guide will walk you through the steps
              to set up your account, verify your details, and start exploring
              the platform.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 1: Access Workbrook
          </h2>
          <p className="ml-2">
            Download the Workbrook mobile app from the Play Store/App Store, or
            visit the web portal at web.workbrook.com.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">Step 2: Sign Up</h2>
          <p className="ml-2">
            Create an account using your email address and a secure password.
            Ensure you use a valid email you can access.
          </p>
          
          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/Fhc_5uvYt-4" 
              title="Welcome & Signup" 
              youtubeUrl="https://www.youtube.com/watch?v=Fhc_5uvYt-4"
            />
          </div>

          <h2 className="ml-2 onboarding-subheader-text">
            Step 3: Verify Your Account
          </h2>
          <p className="ml-2">
            Check your email for an activation code and enter it in the app/web
            portal.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 4: Complete Your Profile
          </h2>
          <p className="ml-2">
            Fill in your personal details, job title, skills, education, and
            work experience. A complete profile increases your chances of
            success on Workbrook. Verify your phone number as well to secure
            your account.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/Jirj56PwwIM" 
              title="Completing Your Profile" 
              youtubeUrl="https://www.youtube.com/watch?v=Jirj56PwwIM"
            />
          </div>

          <h2 className="ml-2 onboarding-subheader-text">
            Step 5: Explore Your Dashboard
          </h2>
          <p className="ml-2">
            Once setup is complete, navigate to your dashboard to explore job
            opportunities (for job seekers) or start posting jobs (for
            employers).
          </p>
          <p className="ml-2">Tips for Success:</p>
          <ol className="ml-5">
            <li>
              Keep your profile 100% complete.Use accurate keywords when filling
              in skills and job postings.
            </li>
            <li>
              Use accurate keywords when filling in skills and job postings.
            </li>
            <li>Verify your phone number and email to build credibility.</li>
            <li>Update your CV and experience regularly.</li>
          </ol>
        </div>

        <div id="job-seekers" className="mt-2 pl-2">
          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">Job Seekers Guide</h2>
            <p className="ml-2">
              Workbrook makes it easy for you to find and apply for jobs.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 1: Upload Your CV
          </h2>
          <p className="ml-2">Upload your latest CV to your profile.</p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 2: Browse and Search for Jobs
          </h2>
          <p className="ml-2">
            Use the job search filters to find roles that fit your skills and
            location.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 3: Apply for Jobs
          </h2>
          <p className="ml-2">
            Click Apply on any job posting, attach your CV (if required), and
            submit.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/yCP_tf7xGLk" 
              title="Applying for Jobs" 
              youtubeUrl="https://www.youtube.com/watch?v=yCP_tf7xGLk"
            />
          </div>

          <h2 id="insights" className="ml-2 onboarding-subheader-text">
            Step 4:Track Application Status
          </h2>
          <p className="ml-2">
            From your Insights, monitor the status of your applications
            (applied, under review, hired, or rejected).
          </p>

          
        </div>

        <div id="employers" className="mt-2 pl-2">
          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">
              Employers / Recruiters Guide
            </h2>
            <p className="ml-2">
              Workbrook helps employers post jobs and find qualified candidates
              quickly.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 1: Request to post a Job
          </h2>
          <p className="ml-2">
            Click Post Job, fill in job details, requirements, and deadlines.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/t1TLk9JV4vk" 
              title="Request to Post a Job" 
              youtubeUrl="https://www.youtube.com/watch?v=t1TLk9JV4vk"
            />
          </div>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/ySWY8zDf72M" 
              title="Post a Job" 
              youtubeUrl="https://www.youtube.com/watch?v=ySWY8zDf72M"
            />
          </div>

          <h2 className="ml-2 onboarding-subheader-text">
            Step 2: Manage Job Listings
          </h2>
          <p className="ml-2">
            From your My Jobs dashboard, track active postings, edit details,
            and view performance metrics.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/ePbI534O93s" 
              title="My Jobs" 
              youtubeUrl="https://www.youtube.com/watch?v=ePbI534O93s"
            />
          </div>

          <h2 className="ml-2 onboarding-subheader-text">
            Step 3: Manage Applicants
          </h2>
          <p className="ml-2">
            Review applicants' CVs, shortlist, and contact candidates directly
            through the platform.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/6vuIT-TRH0Q"
              title="Manage Job Applicants" 
              youtubeUrl="https://www.youtube.com/watch?v=6vuIT-TRH0Q"
            />
          </div>

          <h2 className="ml-2 onboarding-subheader-text">
            Step 4: Track Job Posting Performance
          </h2>
          <p className="ml-2">
            Check how many people viewed, applied, or were shortlisted.
          </p>

          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">
              Job Posting Tiers on Workbrook
            </h2>
            <p className="ml-2">
              Workbrook offers three job posting tiers designed to meet different hiring needs.
 This section explains Tier 2 and Tier 3, their cost structures, and referral benefits.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Tier 2 Jobs
          </h2>
          <p className="ml-2">
           Tier 2 job postings allow employers to control their ad visibility and spending.
           When posting a Tier 2 job:
           <ul>
            <li>You can set a daily budget for how much you want to spend on promoting the job.</li>
            <li>The cost per click (CPC) for applicants viewing your job is set at $0.10.</li>
            <li>This means you only pay when someone clicks to view your job posting.</li>
           </ul>
          </p>
          <h3 className="ml-2 onboarding-subheader-text">
            Referral Rewards:
          </h3>
          <p className="ml-2">
            Tier 2 jobs are <strong>not eligible</strong> for referral rewards.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Tier 3 Jobs
          </h2>
          <p className="ml-2">
            Tier 3 job postings provide unlimited views and applications, making them ideal for high-value or executive roles.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Key Details:
          </h2>
          <ul>
            <li>You can pause, hold, or close the job posting at any time.</li>
            <li>Cost is per hire — not per view.</li>
            <li>A 10% placement fee is charged based on the market median annual salary for that job level and industry.</li>
            <li>You must pay 30% of this placement fee upfront before posting the job.</li>
            <li>After hiring a candidate, you must pay the remaining balance immediately.</li>
            <li>You must pay 30% of this placement fee upfront before posting the job.</li>
            <li>After hiring a candidate, you must pay the remaining balance immediately.</li>
          </ul>

          <h3 className="ml-2 onboarding-subheader-text">
            Referral Rewards:
          </h3>

          <ul className="ml-2">
            <li>Tier 3 jobs can be identified by the "Get Paid" tag on the job card.</li>
            <li>Successful referrals are rewarded — you can track them on your My Jobs Dashboard.</li>
            <li>If your referral is successfully hired, you'll receive "X%" of the placement fee after "X days".</li>
            <li>If your referred candidate resigns within "X days", you'll no longer be eligible for payment.</li>
            <li>Always confirm the salary agreed with your referral before submission.</li>
          </ul>

        </div>

        <div id="referring" className="mt-2 pl-2">
          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">
              Referring Candidates on Workbrook
            </h2>
            <p className="ml-2">
              Workbrook also allows you to earn rewards by referring others to
              jobs.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 1: Open a Job Posting
          </h2>
          <p className="ml-2">
            When you view a tier 2 or 3 job, you will see three options:Like,
            Apply and Refer.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 2: Select "Refer"
          </h2>
          <p className="ml-2">
            Click Refer if you know someone who would be a strong candidate for
            the role.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 3: Share the Job
          </h2>
          <p className="ml-2">Copy or Share the referral link provided.</p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 4: Track Progress
          </h2>
          <p className="ml-2">
            From your dashboard, monitor the status of your referrals (applied,
            interview, hired, or rejected).
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 5: Earn Rewards
          </h2>
          <p className="ml-2">
            If your referral is successfully hired, you earn a small
            commission/percentage as a reward. You can see your rewards in your transaction history.
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/-Lmnb1zIqOo" 
              title="Referring a Candidate" 
              youtubeUrl="https://www.youtube.com/watch?v=-Lmnb1zIqOo"
            />
          </div>
        </div>

        <div id="wallet" className="mt-2 pl-2">
          <div className="mt-2">
            <h2 className="ml-2 onboarding-header-text">Wallet & Payments</h2>
            <p className="ml-2">
              The Workbrook wallet allows you to receive rewards, manage your
              funds, and withdraw securely. All referral bonuses and eligible
              earnings flow into your wallet automatically.
            </p>
          </div>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 1: Access Your Wallet
          </h2>
          <p className="ml-2">From your dashboard, navigate to Wallet.</p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 2: Understand Your Balances
          </h2>
          <ol className="ml-5">
            <li>
              Book Balance: Funds that are pending (e.g., referral reward
              awaiting hire confirmation).
            </li>
            <li>
              Available Balance: Funds that are cleared and can be withdrawn or
              used.
            </li>
          </ol>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 3: Add Funds via JaraPay
          </h2>
          <p className="ml-2">
            Click on JaraPay, Enter an amount and description
          </p>
          <p className="ml-2">Download Jarapay app or Scan code to pay</p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 4: Withdraw Your Funds
          </h2>
          <p className="ml-2">
            Click Withdraw, add your account details and confirm the request.
          </p>
          <h2 className="ml-2 onboarding-subheader-text">
            Step 5: Track Transactions
          </h2>
          <p className="ml-2">
            Check your wallet history to view all credits (earnings, rewards)
            and debits (withdrawals).
          </p>

          <div className="video-card ml-2">
            <VideoEmbed 
              src="https://www.youtube.com/embed/MgPn_JG2auw" 
              title="Wallet & Payment System" 
              youtubeUrl="https://www.youtube.com/watch?v=MgPn_JG2auw"
            />
          </div>
        </div>

        <h1 className="ml-2">Conclusion</h1>
        <p className="ml-2">
          Workbrook is designed to make job applications, hiring, and referrals
          seamless. Whether you're a job seeker, an employer, or a referrer,
          completing your profile and actively engaging with the platform
          ensures you get the most value.
        </p>
      </div>
    </Layout>
  );
};

export default Onboarding;